import { HttpClient, HttpContext, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, catchError, finalize, map, Observable, shareReplay, Subject, throwError } from 'rxjs';
import * as CryptoTS from 'crypto-ts';
import { HttpRes } from '../../../models/common/http.model';
import {
  common_urls,
  registration_urls,
  fixture_urls,
  schedular_urls,
  award_urls,
  cms_urls,
  score_urls,
  resource_permissions_urls,
  accomodation_urls,
  rnr_end_points,
  footer_urls,
} from '../Constants/api-constants';
import { MatSnackBar } from '@angular/material/snack-bar';
import { VideosParams } from '../reg-service/reg-service';
import { EventsSlots } from '../../../models/common/events_payloads';
import { AuthSignalService } from '../auth-signal.service';
import { environment } from '../../../../environments/environment';
import { getStoredAccessToken } from '../../auth-session.util';
import { SKIP_GLOBAL_LOADER } from '../loading/skip-global-loader.context';
import { EVENT_TYPE_CODE } from '../../event-type/event-type.constants';

const httpOptions = {
  headers: new HttpHeaders({
    'Content-Type': 'application/json',
  }),
  observe: 'response' as 'response',
};
const httpAuthOptions = {
  headers: new HttpHeaders({
    'Content-Type': 'application/json',
  }),
  observe: 'response' as 'response',
};

export type ViewTeamDetailsSource = 'leaderboard' | 'participants';

/** Visible map frame sent to `get_events` as viewport_south/west/north/east. */
export interface EventsViewport {
  readonly south: number;
  readonly west: number;
  readonly north: number;
  readonly east: number;
}

/** Snapshot of a category leaderboard row for team performance stats. */
export interface ViewTeamLeaderboardStatsRow {
  total_matches?: number;
  total_matches_won?: number;
  total_matches_lost?: number;
  matches_tie?: number;
  total_sets_won?: number;
  total_sets_lost?: number;
  total_points_won?: number;
  total_points_lost?: number;
  total_points?: number;
  golden_matches_won?: number;
  id?: number;
  participant_id?: number;
  participant?: {
    id?: number;
    participant_name?: string;
  };
}

interface ViewTeamDetails {
  is_view_team_details: boolean;
  team_id: number;
  /** Where the user opened team profile from (controls back label / navigation). */
  source?: ViewTeamDetailsSource;
  /** Leaderboard row when opened from Template 1 leaderboard (stats already loaded). */
  leaderboardRow?: ViewTeamLeaderboardStatsRow;
}
@Injectable({
  providedIn: 'root',
})
export class CommonServiceService {
  private toEventVenueIdsParam(eventVenueIds: any): string {
    if (Array.isArray(eventVenueIds)) {
      return eventVenueIds.filter((id: any) => id != null && id !== '').join(',');
    }
    return eventVenueIds != null && eventVenueIds !== '' ? String(eventVenueIds) : '';
  }

  private getSwitchUserRoleId(): string {
    if (typeof window === 'undefined' || typeof window.sessionStorage === 'undefined') {
      return '';
    }
    return (
      sessionStorage.getItem('switch_user_role_id') ||
      sessionStorage.getItem('switch_user_id') ||
      ''
    );
  }

  private readonly tenantCode = environment.tenant_Name?.toUpperCase() || '';
  environment = environment;
  private readonly getConfigUrl = footer_urls.get_footer_data.replace(
    '{tenant_code}',
    this.tenantCode,
  );
  getPartcipantAndCat(event_id: any, arg1: string) {
    throw new Error('Method not implemented.');
  }
  private valueSubject = new BehaviorSubject<number>(0);

  // Timestamp caching properties
  private readonly CACHE_DURATION = 60 * 1000; // 1 minute in milliseconds
  private readonly TIMESTAMP_COOKIE_NAME = 'api_timestamp';
  private readonly TIMESTAMP_TIME_COOKIE_NAME = 'api_timestamp_time';

  /**
   * Sets a cookie with the given name, value, and expiration time
   * @param name Cookie name
   * @param value Cookie value
   * @param expirationTime Expiration time in milliseconds
   */
  private setCookie(name: string, value: string, expirationTime: number): void {
    if (typeof document !== 'undefined') {
      const expires = new Date(expirationTime).toUTCString();
      document.cookie = `${name}=${value}; expires=${expires}; path=/`;
    }
  }

  /**
   * Gets a cookie value by name
   * @param name Cookie name
   * @returns Cookie value or null if not found
   */
  private getCookie(name: string): string | null {
    if (typeof document === 'undefined') {
      return null;
    }

    const nameEQ = name + '=';
    const ca = document.cookie.split(';');

    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
    }

    return null;
  }

  /**
   * Generates a UUID v4 using crypto-ts
   * @returns string UUID
   */
  private generateUUID(): string {
    // Generate random bytes using crypto-ts
    const randomBytes = CryptoTS.lib.WordArray.random(16);

    // Set version (4) and variant bits
    randomBytes.words[0] = (randomBytes.words[0] & 0xffffffff) | 0x40000000;
    randomBytes.words[1] = (randomBytes.words[1] & 0x3fffffff) | 0x80000000;

    // Convert to hex string and format as UUID
    const hex = randomBytes.toString(CryptoTS.enc.Hex);
    return [
      hex.substr(0, 8),
      hex.substr(8, 4),
      hex.substr(12, 4),
      hex.substr(16, 4),
      hex.substr(20, 12),
    ].join('-');
  }

  /**
   * Gets a cached timestamp that resets every minute using cookies
   * @returns string timestamp with UUID
   */
  private getCachedTimestamp(): string {
    const now = Date.now();

    // Try to get cached timestamp and its creation time from cookies
    const cachedTimestamp = this.getCookie(this.TIMESTAMP_COOKIE_NAME);
    const cachedTimeStr = this.getCookie(this.TIMESTAMP_TIME_COOKIE_NAME);

    // If no cached timestamp or cache has expired, create a new one
    if (
      !cachedTimestamp ||
      !cachedTimeStr ||
      now - parseInt(cachedTimeStr) >= this.CACHE_DURATION
    ) {
      const uuid = this.generateUUID();
      const newTimestamp = `${now}_${uuid}`;
      const expirationTime = now + this.CACHE_DURATION;

      // Store new timestamp and its creation time in cookies
      this.setCookie(this.TIMESTAMP_COOKIE_NAME, newTimestamp, expirationTime);
      this.setCookie(
        this.TIMESTAMP_TIME_COOKIE_NAME,
        now.toString(),
        expirationTime,
      );

      return newTimestamp;
    }

    return cachedTimestamp;
  }
  private apiResponseSubject = new BehaviorSubject<any>(null);
  private team_banner = new BehaviorSubject<string>('');
  private home_to_particpant = new BehaviorSubject<any>(null);
  private is_member_dialod = new BehaviorSubject<any>(false);

  is_member_dialod_value$ = this.is_member_dialod.asObservable();

  team_banner_value$ = this.team_banner.asObservable();
  home_to_particpant_value$ = this.home_to_particpant.asObservable();

  login_subject = this.authSignalService.login_subject;
  forgot_password_subject = this.authSignalService.forgot_password_subject;
  signup_subject = this.authSignalService.signup_subject;
  is_login_success = this.authSignalService.is_login_success;
  openEditProfilePopup$ = this.authSignalService.openEditProfilePopup$;
  profileGatePopupRequest$ = this.authSignalService.profileGatePopupRequest$;

  /**
   * Global payment return popup (`app.component`): closed (back, backdrop, etc.).
   * Subscribers refresh cart or lists when returning from Stripe on any route.
   */
  paymentReturnPopupDismissed$ = new Subject<void>();

  /** Global payment return popup: Retry with `orderId`. */
  paymentReturnPopupRetry$ = new Subject<number>();

  view_team_details = new BehaviorSubject<ViewTeamDetails>({
    is_view_team_details: false,
    team_id: 0,
  });

  schedule_tab_id = new BehaviorSubject<number>(1);

  emitLoginSuccess(value: boolean) {
    this.login_subject.next(value);
  }

  set_member_dialog(value: any) {
    this.is_member_dialod.next(value);
  }

  set_team_banner(value: any) {
    this.team_banner.next(value);
  }
  constructor(
    private httpClient: HttpClient,
    private snack_bar: MatSnackBar,
    private authSignalService: AuthSignalService,
  ) {}
  setValue(newValue: number): void {
    this.valueSubject.next(newValue);
  }

  getValueObservable() {
    return this.valueSubject.asObservable();
  }
  getParticipantTypesAndCategories(
    event_id: number,
    category_id: number = 0,
    include_subcategories: boolean = true,
    include_category_restrictions: boolean = true,
  ): Observable<HttpRes> {
    let params = new HttpParams();

    if (event_id > 0) {
      params = params.set('event_id', event_id);
    }
    if (category_id > 0) {
      params = params.set('category_id', category_id);
    }
    params = params.set('include_subcategories', include_subcategories);
    params = params.set(
      'include_category_restrictions',
      include_category_restrictions,
    );

    return this.httpClient
      .get<HttpRes>(common_urls.get_participant_types_and_categories, {
        params,
        ...httpOptions,
      })
      .pipe(
        shareReplay(),
        map((response: any) => {
          return {
            code: response.status,
            msg: response.body.msg,
            data: response.body,
          } as HttpRes;
        }),
      );
  }

  /**
   * Utility function to find a category by category_id in both parent and children arrays
   * This is useful for hierarchical category structures where categories can have sub-categories
   * @param categoryList - The list of categories to search in
   * @param cat_id - The category_id to find
   * @returns The found category object (either parent or child) or null if not found
   */
  findCategoryById(categoryList: any[], cat_id: any): any {
    // First, check if cat_id matches any parent category
    let foundCategory = categoryList.find((x: any) => x.category_id == cat_id);

    // If not found in parent categories, search in children
    if (!foundCategory) {
      for (const category of categoryList) {
        if (category.children && category.children.length > 0) {
          foundCategory = category.children.find(
            (child: any) => child.category_id == cat_id,
          );
          if (foundCategory) {
            break;
          }
        }
      }
    }

    return foundCategory;
  }

  get_events(
    event_id?: any,
    event_status?: any,
    event_type = '',
    search_event: any = '',
    page_num: any = null,
    per_page: any = null,
    event_level: any = '',
    event_start_date: any = '',
    event_end_date: any = '',
    season_id: any = '',
    event_status_filter: any = '',
    fetch_part_count: boolean | null = null,
    sort_by: any = null,
    sort_type: any = null,
    user_role_id: any = null,
    fetch_event_default_venues: boolean | null = null,
    viewport: EventsViewport | null = null,
  ): Observable<HttpRes> {
    let params = new HttpParams();
    if (event_id != 0 && event_id != '') {
      params = params.set('event_ids', event_id);
    }
    if (event_status != 0 && event_status != '' && event_status != null) {
      params = params.set('event_status', event_status);
    }
    if (event_type != '') {
      params = params.set('event_type', event_type);
    }
    if (search_event != '' && search_event != undefined) {
      params = params.set('search', search_event);
    }
    if (page_num != null) {
      params = params.set('page_num', page_num);
    }
    if (per_page != null) {
      params = params.set('per_page', per_page);
    }
    if (event_level != null && event_level != '') {
      params = params.set('event_level', event_level);
    }
    if (event_start_date != null && event_start_date != '') {
      params = params.set('event_start_date', event_start_date);
    }
    if (event_end_date != null && event_end_date != '') {
      params = params.set('event_end_date', event_end_date);
    }
    if (season_id != null && season_id != '') {
      params = params.set('season_ids', season_id);
    }
    if (event_status_filter != null && event_status_filter != '') {
      params = params.set('event_status', event_status_filter);
    }
    if (typeof fetch_part_count === 'boolean') {
      params = params.set('fetch_part_count', fetch_part_count);
    }
    if (sort_by != null && sort_by != '') {
      params = params.set('sort_by', sort_by);
    }
    if (sort_type != null && sort_type != '') {
      params = params.set('sort_type', sort_type);
    }
    if (user_role_id != null && user_role_id != '') {
      params = params.set('user_role_id', user_role_id);
    }
    if (fetch_event_default_venues === true) {
      params = params.set('fetch_event_default_venues', true);
    }
    if (viewport) {
      params = params
        .set('viewport_south', viewport.south)
        .set('viewport_west', viewport.west)
        .set('viewport_north', viewport.north)
        .set('viewport_east', viewport.east);
    }
    return this.httpClient
      .get<HttpRes>(common_urls.get_events, {
        params,
        ...httpOptions,
        ...httpAuthOptions,
      })
      .pipe(
        shareReplay(),
        map((response: any) => {
          return {
            code: response.status,
            msg: response.body?.msg,
            data: response.body?.data,
          } as HttpRes;
        }),
      );
  }

  get_sections(
    published: any = '',
    section_type: any = '',
  ): Observable<HttpRes> {
    let params = new HttpParams();
    if (published != '') {
      params = params.set('published', published);
    }
    if (section_type != '') {
      params = params.set('section_type', section_type);
    }
    // Always filter by is_active=true
    params = params.set('is_active', 'true');

    return this.httpClient
      .get<HttpRes>(common_urls.get_sections, {
        params,
        ...httpOptions,
        ...httpAuthOptions,
      })
      .pipe(
        shareReplay(),
        map((response: any) => {
          return {
            code: response.status,
            msg: response.body.msg,
            data: response.body.data,
          } as HttpRes;
        }),
      );
  }

  //get live scored matches
  get_live_scored_mtches(
    event_id: any,
    category_id: any,
    search: any,
    page_num: any = 1,
    per_page: any = 12,
    participant_type_id: any = null,
    user_role_id: any = null,
    fetch_point_system: any = false,
    only_in_progress: any = false,
  ): Observable<HttpRes> {
    let params = new HttpParams();
    if (event_id != null && event_id != '') {
      params = params.set('event_id', event_id);
    }
    if (category_id != null && category_id != '') {
      params = params.set('category_id', category_id);
    }
    if (search != null && search != '') {
      params = params.set('search', search);
    }
    if (page_num != null && page_num != '') {
      params = params.set('page_num', page_num);
    }
    if (per_page != null && per_page != '') {
      params = params.set('per_page', per_page);
    }
    if (participant_type_id != 0 && participant_type_id != null) {
      params = params.set('participant_type_id', participant_type_id);
    }
    params = params.set('only_published_rounds', 'true');
    params = params.set('load_sub_matches', 'true');
    if (user_role_id != null && user_role_id != '') {
      params = params.set('user_role_id', user_role_id);
    }
    if (fetch_point_system) {
      params = params.set('fetch_point_system', fetch_point_system);
    }
    if (only_in_progress != null && only_in_progress != '') {
      params = params.set('only_in_progress', only_in_progress);
    }
    return this.httpClient
      .get<HttpRes>(common_urls.get_live_scored_matches, {
        params,
        ...httpOptions,
      })
      .pipe(
        map((response: any) => {
          return {
            code: response.status,
            msg: response.body.msg,
            data: response.body,
          } as HttpRes;
        }),
      );
  }
  // .pipe(
  //   shareReplay(),
  //   map((response: any) => {
  //     return {
  //       code: response.status,
  //       msg: response.body.msg,
  //       data: response.body.data,
  //     } as HttpRes;
  //   })
  // );
  update_event_participant(data: any): Observable<HttpRes> {
    const switchUserRoleId = this.getSwitchUserRoleId();
    const payload =
      switchUserRoleId && data && typeof data === 'object'
        ? { ...data, switch_user_role_id: switchUserRoleId }
        : data;

    return this.httpClient
      .put<HttpRes>(
        registration_urls.update_event_participant,
        payload,
        httpAuthOptions,
      )
      .pipe(
        shareReplay(),
        map((response: any) => {
          return {
            code: response.status,
            msg: response.body.msg,
            data: response.body,
          } as HttpRes;
        }),
      );
  }
  toast(meassage: string, type: string) {
    let sb = this.snack_bar.open(meassage, 'close', {
      duration: 3000,
      panelClass: [type],
    });
    sb.onAction().subscribe(() => {
      sb.dismiss();
    });
  }
  setApiResponse(response: any) {
    this.apiResponseSubject = response;
  }

  getApiResponse() {
    return this.apiResponseSubject;
  }

  get_event_categories(
    event_id: any,
    include_subcategories: any = false,
  ): Observable<HttpRes> {
    let params = new HttpParams();
    if (event_id != 0 && event_id != '') {
      params = params.set('event_id', event_id);
    }

    params = params.set('sort_by_id', true);
    params = params.set('include_subcategories', true);
    return this.httpClient
      .get<HttpRes>(common_urls.get_event_categories, {
        params,
        ...httpOptions,
      })
      .pipe(
        shareReplay(),
        map((response: any) => {
          return {
            code: response.status,
            msg: response.body.msg,
            data: response.body.data,
          } as HttpRes;
        }),
      );
  }
  /** Divisions for venue management: flat categories (no subcategories). */
  get_event_categories_no_subcategories(event_id: any): Observable<HttpRes> {
    let params = new HttpParams();
    if (event_id != 0 && event_id != '') {
      params = params.set('event_id', event_id);
    }
    params = params.set('sort_by_id', true);
    params = params.set('include_subcategories', false);
    return this.httpClient
      .get<HttpRes>(common_urls.get_event_categories, {
        params,
        ...httpOptions,
      })
      .pipe(
        shareReplay(),
        map((response: any) => {
          return {
            code: response.status,
            msg: response.body.msg,
            data: response.body.data,
          } as HttpRes;
        }),
      );
  }

  get_event_categories_for_home(event_id: any): Observable<HttpRes> {
    let params = new HttpParams();
    if (event_id != 0 && event_id != '') {
      params = params.set('event_id', event_id);
    }

    params = params.set('overview', true);
    params = params.set('sort_by_id', true);

    return this.httpClient
      .get<HttpRes>(common_urls.get_event_categories, {
        params,
        ...httpOptions,
      })
      .pipe(
        shareReplay(),
        map((response: any) => {
          return {
            code: response.status,
            msg: response.body.msg,
            data: response.body.data,
          } as HttpRes;
        }),
      );
  }
  get_upcoming_matches(
    event_id?: any,
    upcoming_only?: any,
    limit?: any,
    offset?: any,
    participant_ids: any = null,
    load_live_and_completed: any = null,
    load_sub_matches: any = null,
    only_published_rounds: boolean = true,
    load_event_official_data = false,
  ): Observable<HttpRes> {
    let params = new HttpParams();
    if (event_id != 0 && event_id != '') {
      params = params.set('match_event_id', event_id);
    }

    if (upcoming_only != null && upcoming_only != '') {
      params = params.set('upcoming_only', upcoming_only);
    }
    params = params.set('sort_by', 'datetime');
    params = params.set('sort_type', 'asc');
    if (limit != 0 && limit != '') {
      params = params.set('limit', limit);
    }
    if (offset != 0 && offset != '') {
      params = params.set('offset', offset);
    }
    if (participant_ids != 0 && participant_ids != null) {
      params = params.set('participant_ids', participant_ids);
    }
    if (load_live_and_completed != null && load_live_and_completed != '') {
      params = params.set('load_live_and_completed', load_live_and_completed);
    }
    if (load_sub_matches != null && load_sub_matches != '') {
      params = params.set('load_sub_matches', load_sub_matches);
    }
    if (only_published_rounds != null) {
      params = params.set(
        'only_published_rounds',
        only_published_rounds.toString(),
      );
    }
    if (load_event_official_data) {
      params = params.set('load_event_official_data', true);
    }

    return this.httpClient
      .get<HttpRes>(common_urls.get_upcoming_events, {
        params,
        ...httpOptions,
        ...httpAuthOptions,
      })
      .pipe(
        shareReplay(),
        map((response: any) => {
          return {
            code: response.status,
            msg: response.body.msg,
            data: response.body,
          } as HttpRes;
        }),
      );
  }
  get_schedule_slots(payload: any) {
    let params = new HttpParams();
    params = params.set('only_published_rounds', 'true');
    return this.httpClient
      .post<HttpRes>(common_urls.get_day_schedule, payload, {
        params,
        ...httpOptions,
      })
      .pipe(
        shareReplay(),
        map((response: any) => {
          return {
            code: response.status,
            msg: response.body,
            data: response.body,
          } as HttpRes;
        }),
      );
  }
  get_stages(
    stage_id: any = '',
    event_category_id: any = '',
    event_id: any = '',
    include_participants_count: any = null,
    fetch_rule_settings: any = null,
  ): Observable<HttpRes> {
    let params = new HttpParams();
    if (stage_id != '') {
      params = params.set('stage_id', stage_id);
    }
    if (event_category_id != '') {
      params = params.set('event_category_id', event_category_id);
    }
    if (event_id != '') {
      params = params.set('event_id', event_id);
    }
    if (include_participants_count != null) {
      params = params.set(
        'include_participants_count',
        include_participants_count,
      );
    }
    if (fetch_rule_settings != null) {
      params = params.set('fetch_rule_settings', fetch_rule_settings);
    }
    return this.httpClient
      .get<HttpRes>(common_urls.get_stages, {
        params,
        ...httpOptions,
        ...httpAuthOptions,
      })
      .pipe(
        shareReplay(),
        map((response: any) => {
          return {
            code: response.status,
            msg: response,
            data: response.body.data,
          } as HttpRes;
        }),
      );
  }
  getVenues(event_id: any) {
    let params = new HttpParams();
    if (event_id > 0) {
      params = params.set('event_id', event_id);
    }
    params = params.set('include_registrations', true);
    params = params.set('fetch_reserved_tables', true);
    return this.httpClient
      .get<HttpRes>(common_urls.get_venues, { params, ...httpOptions })
      .pipe(
        shareReplay(),
        map((response: any) => {
          return {
            code: response.status,
            msg: response.body.msg,
            data: response.body,
          } as HttpRes;
        }),
      );
  }
  getEventVenueDays(
    event_id: any,
    id: any = 0,
    year: any = 0,
    venueId: any = 0,
  ) {
    let params = new HttpParams();
    const eventVenueIds = this.toEventVenueIdsParam(venueId);
    if (event_id > 0) {
      params = params.set('event_id', event_id);
    }

    if (id > 0) {
      params = params.set('month', id);
    }
    if (year > 0) {
      params = params.set('year', year);
    }
    if (eventVenueIds) {
      params = params.set('event_venue_ids', eventVenueIds);
    }
    //here i am
    return this.httpClient
      .get<HttpRes>(common_urls.get_event_venue_days, {
        params,
        ...httpOptions,
        ...httpAuthOptions,
      })
      .pipe(
        shareReplay(),
        map((response: any) => {
          return {
            code: response.status,
            msg: response.body.msg,
            data: response.body,
          } as HttpRes;
        }),
      );
  }

  get_rounds(event_id: any, response_format: any) {
    let params = new HttpParams();
    if (event_id != '') {
      params = params.set('event_id', event_id);
    }
    if (response_format != '') {
      params = params.set('response_format', response_format);
    }
    return this.httpClient
      .get<HttpRes>(common_urls.get_rounds, {
        params,
        ...httpOptions,
        ...httpAuthOptions,
      })
      .pipe(
        shareReplay(),
        map((response: any) => {
          return {
            code: response.status,
            msg: response.body,
            data: response.body,
          } as HttpRes;
        }),
      );
  }
  get_team_match_guide(event_id: any, participant_id: any) {
    let params = new HttpParams();
    if (event_id != '') {
      params = params.set('event_id', event_id);
    }
    if (participant_id != '') {
      params = params.set('participant_id', participant_id);
    }
    return this.httpClient
      .get<HttpRes>(common_urls.get_match_guuide, {
        params,
        ...httpOptions,
        ...httpAuthOptions,
      })
      .pipe(
        shareReplay(),
        map((response: any) => {
          return {
            code: response.status,
            msg: response.body,
            data: response.body,
          } as HttpRes;
        }),
      );
  }

  // get videos
  // get_videos(event_id: any = null, object_type: any= null,search:any=null) {
  get_videos({
    object_type,
    event_id,
    object_id,
    status,
    search,
    limit,
    page_num,
    participant_type,
    event_ids,
    category_ids,
    video_status,
    name,
    video_id,
    user_role_id,
  }: VideosParams) {
    let params = new HttpParams();
    if (event_id !== null && event_id !== undefined && event_id !== '') {
      params = params.set('event_id', event_id);
    }
    if (
      object_type !== null &&
      object_type !== undefined &&
      object_type !== ''
    ) {
      params = params.set('object_type', object_type);
    }
    if (search !== null && search !== undefined && search.trim() !== '') {
      params = params.set('search', search);
    }
    if (object_id !== null && object_id !== undefined) {
      params = params.set('object_id', object_id);
    }
    if (status !== null && status !== undefined && status !== '') {
      params = params.set('status', status);
    }
    if (limit !== null && limit !== undefined) {
      params = params.set('limit', limit);
    }
    if (page_num !== null && page_num !== undefined) {
      params = params.set('page_num', page_num);
    }
    if (
      participant_type !== null &&
      participant_type !== undefined &&
      participant_type !== 0
    ) {
      params = params.set('participant_type', participant_type);
    }
    if (
      event_ids !== null &&
      event_ids !== undefined &&
      Array.isArray(event_ids) &&
      event_ids.length > 0
    ) {
      params = params.set('event_ids', event_ids.join(','));
    }
    if (
      category_ids !== null &&
      category_ids !== undefined &&
      Array.isArray(category_ids) &&
      category_ids.length > 0
    ) {
      params = params.set('category_ids', category_ids.join(','));
    }
    if (
      video_status !== null &&
      video_status !== undefined &&
      Array.isArray(video_status) &&
      video_status.length > 0
    ) {
      params = params.set('video_status', video_status.join(','));
    }
    if (name !== null && name !== undefined && name.trim() !== '') {
      params = params.set('name', name);
    }
    if (user_role_id !== null && user_role_id !== undefined) {
      params = params.set('user_role_id', user_role_id);
    }
    // if (true) {
    //   const timestamp = this.getCachedTimestamp();
    //   params = params.set('identifier', timestamp);
    // }
    if (video_id !== null && video_id !== undefined) {
      params = params.set('video_id', video_id);
    }
    // if (true) {
    //   const timestamp = this.getCachedTimestamp();
    //   params = params.set('identifier', timestamp);
    // }
    return this.httpClient
      .get<HttpRes>(common_urls.get_videos, {
        params,
        ...httpOptions,
        ...httpAuthOptions,
      })
      .pipe(
        shareReplay(),
        map((response: any) => {
          return {
            code: response.status,
            msg: response.body.detail.message,
            data: response.body.detail.data,
          } as HttpRes;
        }),
      );
  }

  getDelegationPlayerAccommodationDetails(
    event_id: any,
    user_role_id: any,
    booking_statuses: number[],
  ): Observable<HttpRes> {
    let params = new HttpParams();
    if (event_id) {
      params = params.set('event_id', event_id);
    }
    if (user_role_id) {
      params = params.set('user_role_id', user_role_id);
    }

    if (booking_statuses && booking_statuses.length > 0) {
      params = params.set('booking_statuses', booking_statuses.join(','));
    }

    return this.httpClient
      .get<HttpRes>(accomodation_urls.delegation_player_details, {
        params,
        ...httpOptions,
        ...httpAuthOptions,
      })
      .pipe(
        map((response: any) => {
          return {
            code: response.status,
            msg: response.body.msg,
            data: response.body,
          } as HttpRes;
        }),
      );
  }

  getMatches(
    stage_id: any,
    leg: any,
    response_format: any,
    only_unscheduled: any,
    search: any = '',
  ): Observable<HttpRes> {
    let params = new HttpParams();
    if (stage_id != '') {
      params = params.set('stage_id', stage_id);
    }
    if (leg) {
      params = params.set('leg', leg);
    }
    if (response_format != '') {
      params = params.set('response_format', response_format);
    }
    if (only_unscheduled != '') {
      params = params.set('only_unscheduled', only_unscheduled);
    }
    if (search != '') {
      params = params.set('search', search);
    }

    return this.httpClient
      .get<HttpRes>(common_urls.get_matches, { params, ...httpOptions })
      .pipe(
        shareReplay(),
        map((response: any) => {
          return {
            code: response.status,
            msg: response.body,
            data: response.body,
          } as HttpRes;
        }),
      );
  }

  get_schedule_Matches(params: {
    stage_id?: number;
    event_id?: number;
    dates?: string;
    category_ids?: number;
    stage_ids?: number;
    table_ids?: number;
    venue_ids?: number;
    limit?: number;
    search?: string;
    status?: string;
    parents?: string;
    start_time?: string;
    end_time?: string;
    match_ids?: number[];
    include_stage_details?: boolean;
    fetch_point_system?: boolean;
  }): Observable<HttpRes> {
    let httpprams = new HttpParams();

    if (params.stage_id) {
      httpprams = httpprams.set('stage_id', params.stage_id);
    }
    if (params.event_id) {
      httpprams = httpprams.set('event_id', params.event_id);
    }

    if (params.dates) {
      httpprams = httpprams.set('dates', params.dates);
    }
    if (params.category_ids) {
      httpprams = httpprams.set('category_ids', params.category_ids);
    }
    if (params.venue_ids) {
      httpprams = httpprams.set('venue_ids', params.venue_ids);
    }
    if (params.stage_ids) {
      httpprams = httpprams.set('stage_ids', params.stage_ids);
    }
    if (params.table_ids) {
      httpprams = httpprams.set('table_ids', params.table_ids);
    }
    if (params.search) {
      httpprams = httpprams.set('search', params.search);
    }
    if (params.include_stage_details) {
      httpprams = httpprams.set(
        'include_stage_details',
        params.include_stage_details,
      );
    }
    if (params.fetch_point_system) {
      httpprams = httpprams.set(
        'fetch_point_system',
        params.fetch_point_system,
      );
    }
    if (params.status) {
      httpprams = httpprams.set('status', params.status);
    }
    if (params.parents && params.parents !== '') {
      httpprams = httpprams.set('parents', params.parents);
    }
    if (params.start_time) {
      httpprams = httpprams.set('start_time', params.start_time);
    }

    if (params.end_time) {
      httpprams = httpprams.set('end_time', params.end_time);
    }
    if (params.match_ids) {
      httpprams = httpprams.set('match_ids', params.match_ids.join(','));
    }
    httpprams = httpprams.set('sort_by', 'date_time_table');
    httpprams = httpprams.set('sort_type', 'asc');
    httpprams = httpprams.set('only_scheduled', true);
    httpprams = httpprams.set('has_both_participants', true);
    httpprams = httpprams.set('load_event_official_data', true);
    httpprams = httpprams.set('load_defualt_sort', true);
    httpprams = httpprams.set('load_sub_matches', true);
    httpprams = httpprams.set('include_stage_data', true);

    return this.httpClient
      .get<HttpRes>(common_urls.get_matches, {
        params: httpprams,
        ...httpOptions,
        ...httpAuthOptions,
      })
      .pipe(
        shareReplay(),
        map((response: any) => {
          return {
            code: response.status,
            msg: response.msg,
            data: response.body,
          } as HttpRes;
        }),
      );
  }
  get_schedule_search_Matches(params: {
    stage_id?: number;
    date?: string;
    category_ids?: number;
    stage_ids?: number;
    table_ids?: number;
    limit?: number;
    search?: string;
    status?: string;
    parents?: string;
    start_time?: string;
    end_time?: string;
  }): Observable<HttpRes> {
    let httpprams = new HttpParams();

    if (params.stage_id) {
      httpprams = httpprams.set('stage_id', params.stage_id);
    }

    if (params.date) {
      httpprams = httpprams.set('dates', params.date);
    }
    if (params.category_ids) {
      httpprams = httpprams.set('category_ids', params.category_ids);
    }
    if (params.stage_ids) {
      httpprams = httpprams.set('stage_ids', params.stage_ids);
    }
    if (params.table_ids) {
      httpprams = httpprams.set('table_ids', params.table_ids);
    }
    if (params.search) {
      httpprams = httpprams.set('search', params.search);
    }

    if (params.status) {
      httpprams = httpprams.set('status', params.status);
    }
    if (params.parents && params.parents !== '') {
      httpprams = httpprams.set('parents', params.parents);
    }
    if (params.start_time) {
      httpprams = httpprams.set('start_time', params.start_time);
    }

    if (params.end_time) {
      httpprams = httpprams.set('end_time', params.end_time);
    }

    httpprams = httpprams.set('sort_by', 'datetime');
    httpprams = httpprams.set('sort_type', 'asc');
    httpprams = httpprams.set('has_both_participants', true);
    httpprams = httpprams.set('load_event_official_data', true);

    return this.httpClient
      .get<HttpRes>(common_urls.get_matches, {
        params: httpprams,
        ...httpOptions,
        ...httpAuthOptions,
      })
      .pipe(
        shareReplay(),
        map((response: any) => {
          return {
            code: response.status,
            msg: response.msg,
            data: response.body,
          } as HttpRes;
        }),
      );
  }
  // get_knockout_matches(stage_id:any,highlight_filters:any): Observable<HttpRes> {
  //   let params = new HttpParams();
  //   if (stage_id) {
  //     params = params.set('stage_id', stage_id);
  //   }
  //   if (highlight_filters != '') {
  //     params=  params.set('highlight_filters', highlight_filters);
  //   }

  //   params = params.set('only_published_rounds', true);

  //   return this.httpClient
  //   .get<HttpRes>(fixture_urls.get_knockout_matches, { params, ...httpOptions ,...httpAuthOptions }).pipe(shareReplay(), map((response: any) => {
  //     return {
  //       code: response.status,
  //       msg: response.body,
  //       data: response.body.data,
  //     } as HttpRes;
  //   })
  //   );
  // }
  get_knockout_matches(stage_id: any = '', filters: any): Observable<HttpRes> {
    let params = new HttpParams();
    if (stage_id != '') {
      params = params.set('stage_id', stage_id);
    }
    const cleanedFilters: any = {};
    if (filters && typeof filters === 'object') {
      Object.keys(filters).forEach((key) => {
        const value = filters[key];
        if (
          value !== undefined &&
          value !== null &&
          value !== '' &&
          !(Array.isArray(value) && value.length === 0)
        ) {
          cleanedFilters[key] = value;
        }
      });

      // ✅ Only add if cleanedFilters is still not empty
      if (Object.keys(cleanedFilters).length > 0) {
        params = params.set(
          'highlight_filters',
          JSON.stringify(cleanedFilters),
        );
      }
    }

    params = params.set('only_published_rounds', 'true');
    // params = params.set('show_schedule_details', 'false');

    return this.httpClient
      .get<HttpRes>(fixture_urls.get_knockout_matches, {
        params,
        ...httpOptions,
        ...httpAuthOptions,
      })
      .pipe(
        shareReplay(),
        map((response: any) => {
          return {
            code: response.status,
            msg: response.body,
            data: response.body.data,
          } as HttpRes;
        }),
      );
  }

  /** Knockout "Search By" results (paginated). Uses the public ott/v1/matches route so it works when logged out. */
  get_knockout_search_matches(
    stage_id: any,
    search_by: string,
    search_value: string,
    limit: number = 10,
    offset: number = 1,
  ): Observable<HttpRes> {
    let params = new HttpParams()
      .set('stage_id', stage_id)
      .set('limit', limit)
      .set('offset', offset)
      .set('only_unscheduled', false)
      // Same as the bracket (get_knockout_matches): never return rounds that are not published.
      .set('only_published_rounds', true)
      .set('load_sub_matches', false)
      .set('is_golden_match', false)
      .set('load_event_official_data', true)
      .set('load_schedule_data', true)
      .set('load_walkover_reason', true)
      .set('has_both_participants', true)
      .set('show_videos', true)
      .set('include_summary', true)
      .set('sort_by', 'scheduler')
      .set('sort_type', 'asc')
      .set('only_accepted_event_officials', true)
      .set('fetch_point_system', true);
    if (search_by && search_value) {
      params = params.set('search_by', search_by);
      params = params.set('search_value', search_value);
    }
    return this.httpClient
      .get<HttpRes>(schedular_urls.get_matches, {
        params,
        ...httpOptions,
        ...httpAuthOptions,
      })
      .pipe(
        map((response: any) => {
          return {
            code: response.status,
            msg: response.body?.msg,
            data: response.body,
          } as HttpRes;
        }),
      );
  }

  /** Ranked groups with their participants (All Groups tab). `search` is the optional plain participant search. */
  get_stage_groups(stage_id: any, search: string = ''): Observable<HttpRes> {
    let params = new HttpParams()
      .set('stage_id', stage_id)
      .set('load_participants', true)
      .set('sort_by_ranking', true);
    if (search) {
      params = params.set('search', search);
    }
    return this.httpClient
      .get<HttpRes>(common_urls.get_groups, {
        params,
        ...httpOptions,
        ...httpAuthOptions,
      })
      .pipe(
        map((response: any) => {
          return {
            code: response.status,
            msg: response.body?.msg,
            data: response.body,
          } as HttpRes;
        }),
      );
  }

  /** Groups (with their matrix) matching a "Search By" term; used to narrow the All Groups tab. */
  get_group_search_matches(
    stage_id: any,
    search_by: string,
    search_value: string,
    search: string = '',
  ): Observable<HttpRes> {
    let params = new HttpParams()
      .set('stage_id', stage_id)
      .set('show_matrix', true)
      .set('search_by', search_by)
      .set('search_value', search_value);
    if (search) {
      params = params.set('search', search);
    }
    return this.httpClient
      .get<HttpRes>(fixture_urls.get_group_matches, {
        params,
        ...httpOptions,
        ...httpAuthOptions,
      })
      .pipe(
        map((response: any) => {
          return {
            code: response.status,
            msg: response.body,
            data: response.body?.data,
          } as HttpRes;
        }),
      );
  }

  get_golden_match(
    stage_id: any,
    leg: any,
    response_format: any,
    only_unscheduled: any,
    search: any = '',
    only_winners: any = null,
    participant_type_id: any = null,
    category_id: any = null,
    round_id: any = null,
    group_id: any = null,
    load_sub_matches: any = null,
    include_golden_matches: any = null,
    load_event_official_data: any = null,
    load_schedule_data: any = null,
    order: any = null,
    load_playing_order: any = null,
    playing_order_id: any = null,
    sort_by: any = 'leg',
    fetch_point_system: any = false,
    /** Suppresses the page-wide loader overlay — for callers that render their
     *  own in-place loading state (e.g. the bracket tile hover popup). */
    skip_global_loader: boolean = false,
  ): Observable<HttpRes> {
    let params = new HttpParams();
    if (stage_id != '') {
      params = params.set('round_id', stage_id);
    }
    if (leg) {
      params = params.set('leg', leg);
    }
    if (response_format != '') {
      params = params.set('response_format', response_format);
    }
    if (only_unscheduled != '') {
      params = params.set('only_unscheduled', only_unscheduled);
    }
    if (search != '') {
      params = params.set('search', search);
    }
    if (only_winners != null) {
      params = params.set('only_winners', only_winners);
    }
    if (participant_type_id != null) {
      params = params.set('participant_type_id', participant_type_id);
    }
    if (category_id != null) {
      params = params.set('category_id', category_id);
    }
    if (round_id != null) {
      params = params.set('round_id', round_id);
    }
    if (group_id != null) {
      params = params.set('group_id', group_id);
    }
    if (load_sub_matches != null) {
      params = params.set('load_sub_matches', load_sub_matches);
    }
    if (include_golden_matches != null) {
      params = params.set('is_golden_match', include_golden_matches);
    }
    if (load_event_official_data != null) {
      params = params.set('load_event_official_data', load_event_official_data);
    }
    if (load_schedule_data != null) {
      params = params.set('load_schedule_data', load_schedule_data);
    }
    if (order != null) {
      params = params.set('order', order);
    }
    if (load_playing_order != null) {
      params = params.set('load_playing_order', load_playing_order);
    }
    if (playing_order_id != null) {
      params = params.set('playing_order_id', playing_order_id);
    }
    if (sort_by != null) {
      params = params.set('sort_by', sort_by);
    }
    if (fetch_point_system !== null && fetch_point_system !== undefined) {
      params = params.set('fetch_point_system', fetch_point_system);
    }
    params = params.set('include_summary', true);
    return this.httpClient
      .get<HttpRes>(schedular_urls.get_matches, {
        params,
        context: new HttpContext().set(SKIP_GLOBAL_LOADER, skip_global_loader),
        ...httpOptions,
        ...httpAuthOptions,
      })
      .pipe(
        shareReplay(),
        map((response: any) => {
          return {
            code: response.status,
            msg: response.body,
            data: response.body,
          } as HttpRes;
        }),
      );
  }

  get_groups(
    stage_id: any = '',
    load_participants: boolean = false,
    search: any = '',
    group_id: any = '',
    event_id: any = '',
    name: any = '',
    status: any = '',
    sort_by_ranking = false,
  ): Observable<HttpRes> {
    let params = new HttpParams();
    if (stage_id != 0 && stage_id != '') {
      params = params.set('stage_id', stage_id);
    }
    if (sort_by_ranking) {
      params = params.set('sort_by_ranking', sort_by_ranking);
    }
    if (group_id != 0 && group_id != '') {
      params = params.set('group_id', group_id);
    }
    if (event_id != 0 && event_id != '') {
      params = params.set('event_id', event_id);
    }
    if (name != 0 && name != '') {
      params = params.set('name', name);
    }
    if (status != 0 && status != '') {
      params = params.set('status', status);
    }
    if (search != 0 && search != '') {
      params = params.set('search', search);
    }
    params = params.set('load_participants', load_participants);
    return this.httpClient
      .get<HttpRes>(common_urls.get_groups, {
        params,
        ...httpOptions,
        ...httpAuthOptions,
      })
      .pipe(
        shareReplay(),
        map((response: any) => {
          return {
            code: response.status,
            msg: response.body.msg,
            data: response.body,
          } as HttpRes;
        }),
      );
  }
  get_group_matches(
    stage_id: any = '',
    stage_name: any = 'Groups',
    filters: any = null,
    view: string = 'standard',
    search: any = '',
    sort_by_schedule: boolean = false,
    search_by: string = '',
    search_value: string = '',
  ): Observable<HttpRes> {
    let params = new HttpParams();
    if (stage_id != '') {
      params = params.set('stage_id', stage_id);
    }
    if (view) {
      params = params.set('view', view);
    }
    if (search != null && search != '') {
      params = params.set('search', search);
    }
    if (sort_by_schedule) {
      params = params.set('sort_by_schedule', true);
    }
    if (search_by && search_value) {
      params = params.set('search_by', search_by);
      params = params.set('search_value', search_value);
    }
    const cleanedFilters: any = {};
    if (filters && typeof filters === 'object') {
      Object.keys(filters).forEach((key) => {
        const value = filters[key];
        if (
          value !== undefined &&
          value !== null &&
          value !== '' &&
          !(Array.isArray(value) && value.length === 0)
        ) {
          cleanedFilters[key] = value;
        }
      });

      // ✅ Only add if cleanedFilters is still not empty
      if (Object.keys(cleanedFilters).length > 0) {
        params = params.set(
          'highlight_filters',
          JSON.stringify(cleanedFilters),
        );
      }
    }
    params = params.set('show_matrix', true);
    params = params.set('fetch_point_system', true);
    return this.httpClient
      .get<HttpRes>(fixture_urls.get_group_matches, {
        params,
        ...httpOptions,
        ...httpAuthOptions,
      })
      .pipe(
        shareReplay(),
        map((response: any) => {
          return {
            code: response.status,
            msg: response.body,
            data: response.body.data,
          } as HttpRes;
        }),
      );
  }
  get_sub_matches_matches(
    parent_match_id: any,
    playing_order_id: any,
    is_golden_match: any,
  ): Observable<HttpRes> {
    let params = new HttpParams();
    if (parent_match_id != '') {
      params = params.set('parent_match_id', parent_match_id);
    }
    if (playing_order_id != '') {
      params = params.set('playing_order_id', playing_order_id);
    }
    if (is_golden_match != '') {
      params = params.set('is_golden_match', is_golden_match);
    }
    return this.httpClient
      .get<HttpRes>(fixture_urls.get_team_playing_order_with_matches, {
        params,
        ...httpOptions,
        ...httpAuthOptions,
      })
      .pipe(
        shareReplay(),
        map((response: any) => {
          return {
            code: response.status,
            msg: response.msg,
            data: response.body,
          } as HttpRes;
        }),
      );
  }
  get_matches(
    stage_id: any,
    leg: any,
    response_format: any,
    only_unscheduled: any,
    search: any = '',
    only_winners: any = null,
    participant_type_id: any = null,
    category_id: any = null,
    round_id: any = null,
    group_id: any = null,
    load_sub_matches: any = null,
    include_golden_matches: any = null,
    load_event_official_data: any = null,
    load_schedule_data: any = null,
    load_walkover_reason: any = null,
    has_both_participants: any = null,
    limit: any = '',
    offset: any = '',
    include_summary: any = null,
    skip_matches_in_cart: any = null,
    only_unassigned_event_officials: any = null,
    skip_matches_with_bye: any = null,
    object_type: any = null,
    event_id: any = null,
    event_category_id: any = null,
    official_event_id: any = null,
    fetch_all_event_officials: any = null,
    sort_by: any = null,
    sort_type: any = null,
  ): Observable<HttpRes> {
    let params = new HttpParams();
    if (stage_id != '' && stage_id != undefined) {
      params = params.set('stage_id', stage_id);
    }
    if (leg) {
      params = params.set('leg', leg);
    }
    if (response_format != '') {
      params = params.set('response_format', response_format);
    }
    if (only_unscheduled != '') {
      params = params.set('only_unscheduled', only_unscheduled);
    }
    if (search != '') {
      params = params.set('search', search.trim());
    }
    if (only_winners != null) {
      params = params.set('only_winners', only_winners);
    }
    if (participant_type_id != null) {
      params = params.set('participant_type_id', participant_type_id);
    }
    if (category_id != null) {
      params = params.set('event_category_id', category_id);
    }
    if (round_id != null) {
      params = params.set('round_id', round_id);
    }
    if (group_id != null) {
      params = params.set('group_id', group_id);
    }
    if (load_sub_matches != null) {
      params = params.set('load_sub_matches', load_sub_matches);
    }
    if (include_golden_matches != null) {
      params = params.set('is_golden_match', include_golden_matches);
    }
    if (load_event_official_data != null) {
      params = params.set('load_event_official_data', load_event_official_data);
    }
    if (load_schedule_data != null) {
      params = params.set('load_schedule_data', load_schedule_data);
    }
    if (load_walkover_reason != null) {
      params = params.set('load_walkover_reason', load_walkover_reason);
    }
    if (has_both_participants != null) {
      params = params.set('has_both_participants', has_both_participants);
    }
    if (limit != '') {
      params = params.set('limit', limit);
    }
    if (offset != '') {
      params = params.set('offset', offset);
    }
    if (include_summary != null) {
      params = params.set('include_summary', include_summary);
    }
    if (skip_matches_in_cart != null) {
      params = params.set(
        'only_available_for_registration',
        skip_matches_in_cart,
      );
    }
    if (only_unassigned_event_officials != null) {
      params = params.set(
        'only_unassigned_event_officials',
        only_unassigned_event_officials,
      );
    }
    if (skip_matches_with_bye != null) {
      params = params.set('skip_matches_with_bye', skip_matches_with_bye);
    }
    if (object_type != null) {
      params = params.set('object_type', object_type);
    }
    if (event_id != null) {
      params = params.set('event_id', event_id);
    }
    if (event_category_id != null) {
      params = params.set('event_category_id', event_category_id);
    }
    if (official_event_id != null) {
      params = params.set('official_event_id', official_event_id);
    }
    if (fetch_all_event_officials != null) {
      params = params.set(
        'fetch_all_event_officials',
        fetch_all_event_officials,
      );
    }
    if (sort_type != null) {
      params = params.set('sort_type', sort_type);
    }
    if (sort_by != null) {
      params = params.set('sort_by', sort_by);
    }
    return this.httpClient
      .get<HttpRes>(schedular_urls.get_matches_auth, {
        params,
        ...httpAuthOptions,
      })
      .pipe(
        shareReplay(),
        map((response: any) => {
          return {
            code: response.status,
            msg: response.body,
            data: response.body,
          } as HttpRes;
        }),
      );
  }
  get_matches_order_history(
    stage_id: any,
    leg: any,
    response_format: any,
    only_unscheduled: any,
    search: any = '',
    only_winners: any = null,
    participant_type_id: any = null,
    category_id: any = null,
    round_id: any = null,
    group_id: any = null,
    load_sub_matches: any = null,
    include_golden_matches: any = null,
    load_event_official_data: any = null,
    load_schedule_data: any = null,
    load_walkover_reason: any = null,
    has_both_participants: any = null,
    limit: any = '',
    offset: any = '',
    include_summary: any = null,
    skip_matches_in_cart: any = null,
    only_unassigned_event_officials: any = null,
    skip_matches_with_bye: any = null,
    object_type: any = null,
    event_id: any = null,
    event_category_id: any = null,
    official_event_id: any = null,
    fetch_all_event_officials: any = null,
    sort_by: any = null,
    sort_type: any = null,
    target: any = null,
  ): Observable<HttpRes> {
    let params = new HttpParams();
    if (stage_id != '' && stage_id != undefined) {
      params = params.set('stage_id', stage_id);
    }
    if (leg) {
      params = params.set('leg', leg);
    }
    if (response_format != '') {
      params = params.set('response_format', response_format);
    }
    if (only_unscheduled != '') {
      params = params.set('only_unscheduled', only_unscheduled);
    }
    if (search != '') {
      params = params.set('search', search.trim());
    }
    if (only_winners != null) {
      params = params.set('only_winners', only_winners);
    }
    if (participant_type_id != null) {
      params = params.set('participant_type_id', participant_type_id);
    }
    if (category_id != null) {
      params = params.set('event_category_id', category_id);
    }
    if (round_id != null) {
      params = params.set('round_id', round_id);
    }
    if (group_id != null) {
      params = params.set('group_id', group_id);
    }
    if (load_sub_matches != null) {
      params = params.set('load_sub_matches', load_sub_matches);
    }
    if (include_golden_matches != null) {
      params = params.set('is_golden_match', include_golden_matches);
    }
    if (load_event_official_data != null) {
      params = params.set('load_event_official_data', load_event_official_data);
    }
    if (load_schedule_data != null) {
      params = params.set('load_schedule_data', load_schedule_data);
    }
    if (load_walkover_reason != null) {
      params = params.set('load_walkover_reason', load_walkover_reason);
    }
    if (has_both_participants != null) {
      params = params.set('has_both_participants', has_both_participants);
    }
    if (limit != '') {
      params = params.set('limit', limit);
    }
    if (offset != '') {
      params = params.set('offset', offset);
    }
    if (include_summary != null) {
      params = params.set('include_summary', include_summary);
    }
    if (skip_matches_in_cart != null) {
      params = params.set(
        'only_available_for_registration',
        skip_matches_in_cart,
      );
    }
    if (only_unassigned_event_officials != null) {
      params = params.set(
        'only_unassigned_event_officials',
        only_unassigned_event_officials,
      );
    }
    if (skip_matches_with_bye != null) {
      params = params.set('skip_matches_with_bye', skip_matches_with_bye);
    }
    if (object_type != null) {
      params = params.set('object_type', object_type);
    }
    if (event_id != null) {
      params = params.set('event_id', event_id);
    }
    if (event_category_id != null) {
      params = params.set('event_category_id', event_category_id);
    }
    if (official_event_id != null) {
      params = params.set('official_event_id', official_event_id);
    }
    if (fetch_all_event_officials != null) {
      params = params.set(
        'fetch_all_event_officials',
        fetch_all_event_officials,
      );
    }
    if (sort_type != null) {
      params = params.set('sort_type', sort_type);
    }
    if (sort_by != null) {
      params = params.set('sort_by', sort_by);
    }
    if (target != null) {
      params = params.set('target', target);
    }

    return this.httpClient
      .get<HttpRes>(schedular_urls.get_matches_auth_custom, {
        params,
        ...httpAuthOptions,
      })
      .pipe(
        shareReplay(),
        map((response: any) => {
          return {
            code: response.status,
            msg: response.body,
            data: response.body,
          } as HttpRes;
        }),
      );
  }
  get_countries(params: { search?: string }): Observable<HttpRes> {
    let httpParams = new HttpParams();
    if (params.search) {
      httpParams = httpParams.set('search', params.search);
    }
    return this.httpClient
      .get<HttpRes>(common_urls.get_countries, {
        params: httpParams,
        ...httpOptions,
        ...httpAuthOptions,
      })
      .pipe(
        shareReplay(),
        map((response: any) => {
          return {
            code: response.status,
            msg: response.msg,
            data: response.body,
          } as HttpRes;
        }),
      );
  }

  get_event_category_rewards(
    event_id: number,
    category_id: number,
  ): Observable<HttpRes> {
    let params = new HttpParams();
    if (event_id != 0) {
      params = params.set('event_id', event_id);
    }
    params = params.set('category_id', category_id);

    return this.httpClient
      .get<HttpRes>(award_urls.get_event_category_rewards, {
        params,
        ...httpOptions,
        ...httpAuthOptions,
      })
      .pipe(
        shareReplay(),
        map((response: any) => {
          return {
            code: response.status,
            msg: response.body.msg,
            data: response.body.award,
          } as HttpRes;
        }),
      );
  }

  getHeadToHeadStats(opponent_id: number): Observable<HttpRes> {
    let params = new HttpParams();
    params = params.set('opponent_id', opponent_id);

    return this.httpClient
      .get<HttpRes>(common_urls.get_head_to_head, {
        params,
        ...httpOptions,
        ...httpAuthOptions,
      })
      .pipe(
        map((response: any) => {
          return {
            code: response.status,
            msg: response.body.msg,
            data: response.body.data,
          } as HttpRes;
        }),
      );
  }

  /**
   * Published awards for Best Player / Order of Merit sub-tabs.
   * @param event_id Event identifier.
   * @param award_type OTT award_type query param.
   * @returns Observable with the awards list payload.
   */
  get_awards_list(
    event_id: number,
    award_type: 'best_player' | 'order_of_merit',
  ): Observable<HttpRes> {
    const params = new HttpParams()
      .set('event_id', event_id)
      .set('award_type', award_type);

    return this.httpClient
      .get<HttpRes>(award_urls.get_awards_standings, {
        params,
        ...httpOptions,
        ...httpAuthOptions,
      })
      .pipe(
        shareReplay(),
        map((response: any) => {
          return {
            code: response.status,
            msg: response.body?.msg,
            data: response.body?.data ?? response.body,
          } as HttpRes;
        }),
      );
  }

  /**
   * Standings for one published award.
   * @param event_id Event identifier.
   * @param award_id Selected published award.
   * @param award_type OTT award_type query param.
   * @param page Page number for paginated responses.
   * @param per_page Page size for paginated responses.
   * @param search Optional participant name filter (server-side).
   * @returns Observable with standing rows for the award.
   */
  get_award_standings(
    event_id: number,
    award_id: number,
    award_type: 'best_player' | 'order_of_merit',
    page = 1,
    per_page = 10,
    search = '',
  ): Observable<HttpRes> {
    let params = new HttpParams()
      .set('event_id', event_id)
      .set('award_type', award_type)
      .set('award_id', award_id)
      .set('page', page)
      .set('per_page', per_page);

    const searchTerm = search.trim();
    if (searchTerm) {
      params = params.set('search', searchTerm);
    }

    return this.httpClient
      .get<HttpRes>(award_urls.get_awards_standings, {
        params,
        ...httpOptions,
        ...httpAuthOptions,
      })
      .pipe(
        shareReplay(),
        map((response: any) => {
          return {
            code: response.status,
            msg: response.body?.msg,
            data: response.body?.data ?? response.body,
          } as HttpRes;
        }),
      );
  }

  get_event_standings(
    event_id: number,
    category_id: number,
    search: string,
    participant_type_id: number,
  ): Observable<HttpRes> {
    let params = new HttpParams();
    if (event_id != 0) {
      params = params.set('event_id', event_id);
    }
    params = params.set('category_id', category_id);
    params = params.set('verified_status', 'ACCEPTED');
    params = params.set('is_queued', false);
    params = params.set('is_active', true);
    params = params.set('loan_player', false);
    params = params.set('participant_type_id', participant_type_id);
    params = params.set('sort_by', 'standing');
    params = params.set('sort_type', 'asc');
    params = params.set('page_num', 1);
    params = params.set('per_page', 1000);
    params = params.set('verifier', false);
    params = params.set('is_deleted', false);
    params = params.set('search', search);
    params = params.set('call_for_standings_ott', true);
    params = params.set('fetch_participant_selected_parent', true);

    return this.httpClient
      .get<HttpRes>(award_urls.get_event_standings, {
        params,
        ...httpOptions,
        ...httpAuthOptions,
      })
      .pipe(
        shareReplay(),
        map((response: any) => {
          return {
            code: response.status,
            msg: response.body.msg,
            data: response.body.data,
          } as HttpRes;
        }),
      );
  }

  get_cms_layouts(): Observable<HttpRes> {
    let params = new HttpParams();

    return this.httpClient
      .get<HttpRes>(cms_urls.get_cms_layouts, {
        params,
        ...httpOptions,
        ...httpAuthOptions,
      })
      .pipe(
        shareReplay(),
        map((response: any) => {
          return {
            code: response.status,
            msg: response.body.msg,
            data: response.body,
          } as HttpRes;
        }),
      );
  }

  get_cms_layout_detail(
    layout_id: number,
    fetch_point_system: any = false,
  ): Observable<HttpRes> {
    let params = new HttpParams();
    params = params.set('section_layout_id', layout_id);
    if (fetch_point_system) {
      params = params.set('fetch_point_system', fetch_point_system);
    }
    // if (layout_id === 31) {
    //   const timestamp = this.getCachedTimestamp();
    //   params = params.set('identifier', timestamp);
    // }
    return this.httpClient
      .get<HttpRes>(cms_urls.get_cms_layout_detail, {
        params,
        ...httpOptions,
        ...httpAuthOptions,
      })
      .pipe(
        shareReplay(),
        map((response: any) => {
          return {
            code: response.status,
            msg: response.body.msg,
            data: response.body,
          } as HttpRes;
        }),
      );
  }

  get_events_slots(payload: Partial<EventsSlots>): Observable<HttpRes> {
    let httpprams = new HttpParams();
    if (
      payload.show_schedule_details != undefined &&
      payload.show_schedule_details != null
    ) {
      httpprams = httpprams.set(
        'show_schedule_details',
        payload.show_schedule_details,
      );
    }
    if (payload.event_id) {
      httpprams = httpprams.set('event_id', payload.event_id);
    }
    if (payload.date) {
      httpprams = httpprams.set('date', payload.date);
    }
    if (payload.stage_id) {
      httpprams = httpprams.set('stage_id', payload.stage_id);
    }
    if (payload.category_ids) {
      httpprams = httpprams.set('category_ids', payload.category_ids);
    }
    if (payload.venue_ids) {
      httpprams = httpprams.set('venue_ids', payload.venue_ids);
    }
    if (payload.stage_ids) {
      httpprams = httpprams.set('stage_ids', payload.stage_ids);
    }
    if (payload.table_ids) {
      httpprams = httpprams.set('table_ids', payload.table_ids);
    }
    if (payload.status) {
      httpprams = httpprams.set('status', payload.status);
    }
    if (payload.parents) {
      httpprams = httpprams.set('participant_parent_filters', payload.parents);
    }
    if (payload.start_time) {
      httpprams = httpprams.set('start_time', payload.start_time);
    }
    if (payload.end_time) {
      httpprams = httpprams.set('end_time', payload.end_time);
    }
    if (payload.search) {
      httpprams = httpprams.set('search', payload.search);
    }
    if (payload.search_by) {
      httpprams = httpprams.set('search_by', payload.search_by);
    }
    if (payload.only_published_rounds) {
      httpprams = httpprams.set(
        'only_published_rounds',
        payload.only_published_rounds,
      );
    }

    return this.httpClient
      .get<HttpRes>(schedular_urls.get_events_slots, {
        params: httpprams,
        ...httpOptions,
        ...httpAuthOptions,
      })
      .pipe(
        shareReplay(),
        map((response: any) => {
          return {
            code: response.status,
            msg: response.body.msg,
            data: response.body.data,
          } as HttpRes;
        }),
      );
  }

  get_new_schedule_Matches(params: {
    stage_id?: number;
    event_id?: number;
    date?: string;
    category_ids?: number;
    stage_ids?: number;
    table_ids?: number;
    venue_ids?: number;
    limit?: number;
    search?: string;
    status?: string;
    parents?: string;
    start_time?: string;
    end_time?: string;
  }): Observable<HttpRes> {
    let httpprams = new HttpParams();

    if (params.stage_id) {
      httpprams = httpprams.set('stage_id', params.stage_id);
    }
    if (params.event_id) {
      httpprams = httpprams.set('event_id', params.event_id);
    }

    if (params.date) {
      httpprams = httpprams.set('dates', params.date);
    }
    if (params.category_ids) {
      httpprams = httpprams.set('category_ids', params.category_ids);
    }
    if (params.venue_ids) {
      httpprams = httpprams.set('venue_ids', params.venue_ids);
    }
    if (params.stage_ids) {
      httpprams = httpprams.set('stage_ids', params.stage_ids);
    }
    if (params.table_ids) {
      httpprams = httpprams.set('table_ids', params.table_ids);
    }
    if (params.search) {
      httpprams = httpprams.set('search', params.search);
    }

    if (params.status) {
      httpprams = httpprams.set('status', params.status);
    }
    if (params.parents && params.parents !== '') {
      httpprams = httpprams.set('parents', params.parents);
    }
    if (params.start_time) {
      httpprams = httpprams.set('start_time', params.start_time);
    }

    if (params.end_time) {
      httpprams = httpprams.set('end_time', params.end_time);
    }

    httpprams = httpprams.set('sort_by', 'date_time_table');
    httpprams = httpprams.set('sort_type', 'asc');
    httpprams = httpprams.set('only_scheduled', true);
    httpprams = httpprams.set('has_both_participants', true);
    httpprams = httpprams.set('load_event_official_data', true);
    httpprams = httpprams.set('load_defualt_sort', true);
    httpprams = httpprams.set('load_sub_matches', true);

    return this.httpClient
      .get<HttpRes>(common_urls.get_matches, {
        params: httpprams,
        ...httpOptions,
        ...httpAuthOptions,
      })
      .pipe(
        shareReplay(),
        map((response: any) => {
          return {
            code: response.status,
            msg: response.msg,
            data: response.body,
          } as HttpRes;
        }),
      );
  }

  get_tie_player(
    participant_id: number,
    group_id: number,
    event_id: number,
    category_id: number,
    stage_id: number,
  ): Observable<HttpRes> {
    let httpprams = new HttpParams();

    if (participant_id) {
      httpprams = httpprams.set('participant_type_id', participant_id);
    }
    if (group_id) {
      httpprams = httpprams.set('group_id', group_id);
    }
    if (event_id) {
      httpprams = httpprams.set('event_id', event_id);
    }
    if (category_id) {
      httpprams = httpprams.set('category_id', category_id);
    }
    if (stage_id) {
      httpprams = httpprams.set('stage_id', stage_id);
    }

    return this.httpClient
      .get<HttpRes>(score_urls.get_tie_players, {
        params: httpprams,
        ...httpOptions,
        ...httpAuthOptions,
      })
      .pipe(
        shareReplay(),
        map((response: any) => {
          return {
            code: response.status,
            msg: response.msg,
            data: response.body,
          } as HttpRes;
        }),
      );
  }
  get_broadcasts(
    event_type: any = null,
    search: any = null,
    start_date: any = null,
    end_date: any = null,
    event_status: any = null,
  ): Observable<HttpRes> {
    let params = new HttpParams();
    if (event_type) {
      params = params.set('event_type', event_type);
    }
    if (search != null) {
      params = params.set('search', search);
    }
    params = params.set('sort_by', 'relevance');
    if (start_date != null && start_date != '') {
      params = params.set('start_date', start_date);
    }
    if (end_date != null && end_date != '') {
      params = params.set('end_date', end_date);
    }
    if (event_status != null && event_status != '') {
      params = params.set('status', event_status);
    }

    return this.httpClient
      .get<HttpRes>(common_urls.get_broadcasts, {
        ...httpOptions,
        ...httpAuthOptions,
        params,
      })
      .pipe(
        shareReplay(),
        map((response: any) => {
          return {
            code: response.status,
            msg: response.msg,
            data: response.body,
          } as HttpRes;
        }),
      );
  }

  get_all_stage_Team_player(
    event_category_id: any,
    participant_id: any = null,
  ): Observable<HttpRes> {
    let params = new HttpParams();

    // Add parameters to match the curl request
    params = params.set('group_by', 'participant');
    params = params.set('offset', '1');
    // if (participant_id) {
    //   params = params.set('participant_id', participant_id);
    // }
    // Build the URL with event_category_id as path parameter
    const url = `${common_urls.get_all_stage_Team_player}${event_category_id}`;

    return this.httpClient.get<HttpRes>(url, { params, ...httpOptions }).pipe(
      shareReplay(),
      map((response: any) => {
        return {
          code: response.status,
          msg: response.body.msg,
          data: response.body,
        } as HttpRes;
      }),
    );
  }
  get_all_stage_Team(event_category_id: any): Observable<HttpRes> {
    let params = new HttpParams();

    // Add required parameters to match the API endpoint
    params = params.set('sort_by_ranking', 'false');
    params = params.set('offset', '1');

    // Build the URL with event_category_id as path parameter
    const url = `${common_urls.get_all_stage_Team}/${event_category_id}`;

    return this.httpClient.get<HttpRes>(url, { params, ...httpOptions }).pipe(
      shareReplay(),
      map((response: any) => {
        return {
          code: response.status,
          msg: response.body.msg,
          data: response.body,
        } as HttpRes;
      }),
    );
  }
  get_selected_stage_Team(
    event_category_id: any,
    stage_id: any = null,
    participant_id: any = null,
    search: any = '',
  ): Observable<HttpRes> {
    let params = new HttpParams();

    // Add required parameters to match the API endpoint
    params = params.set('sort_by_ranking', 'false');
    params = params.set('offset', '1');

    // Add stage_id if provided
    if (stage_id) {
      params = params.set('stage_id', stage_id);
    }

    // Add search term for server-side participant name search
    if (search) {
      params = params.set('search', search);
    }

    // Add participant_id if provided
    // if (participant_id) {
    //   params = params.set('participant_id', participant_id);
    // }
    // Build the URL with event_category_id as path parameter
    const url = `${common_urls.get_all_stage_Team}/${event_category_id}`;

    return this.httpClient.get<HttpRes>(url, { params, ...httpOptions }).pipe(
      shareReplay(),
      map((response: any) => {
        return {
          code: response.status,
          msg: response.body.msg,
          data: response.body,
        } as HttpRes;
      }),
    );
  }

  get_selected_stage_Team_player(
    event_category_id: any,
    stage_id: any = null,
    participant_id: any = null,
    search: any = '',
  ): Observable<HttpRes> {
    let params = new HttpParams();

    // Add parameters to match the curl request
    params = params.set('group_by', 'participant');

    params = params.set('offset', '1');

    // Add stage_id if provided
    if (stage_id) {
      params = params.set('stage_id', stage_id);
    }

    // Add participant_id if provided
    if (participant_id) {
      params = params.set('participant_id', participant_id);
    }

    // Add search term for server-side participant name search
    if (search) {
      params = params.set('search', search);
    }
    // Build the URL with event_category_id as path parameter
    const url = `${common_urls.get_all_stage_Team_player}${event_category_id}`;

    return this.httpClient.get<HttpRes>(url, { params, ...httpOptions }).pipe(
      shareReplay(),
      map((response: any) => {
        return {
          code: response.status,
          msg: response.body.msg,
          data: response.body,
        } as HttpRes;
      }),
    );
  }

  get_matches_leaderboard(
    load_sub_matches: any = true,
    has_both_participants: any = true,
    participant_ids: any = null,
    event_category_id: any = null,
    stage_id: any = null,
    is_golden_match: any = false,
    sort_by: any = null,
    sort_type: any = null,
  ): Observable<HttpRes> {
    let params = new HttpParams();
    // Include the provided arguments as query parameters
    params = params.set('load_sub_matches', String(load_sub_matches));
    params = params.set('has_both_participants', String(has_both_participants));
    params = params.set('is_golden_match', String(is_golden_match));
    if (sort_by != null) {
      params = params.set('sort_by', sort_by);
    }
    if (sort_type != null) {
      params = params.set('sort_type', sort_type);
    }
    // Only append participant_ids if a value is supplied
    if (participant_ids !== null && participant_ids !== undefined) {
      params = params.set('participant_ids', participant_ids);
    }
    if (event_category_id !== null && event_category_id !== undefined) {
      params = params.set('event_category_id', event_category_id);
    }
    if (stage_id !== null && stage_id !== undefined) {
      params = params.set('stage_id', stage_id);
    }

    return this.httpClient
      .get<HttpRes>(common_urls.get_matches, { params, ...httpOptions })
      .pipe(
        shareReplay(),
        map((response: any) => {
          return {
            code: response.status,
            msg: response.body,
            data: response.body,
          } as HttpRes;
        }),
      );
  }

  recalculate_leaderboard(
    event_category_id: any,
    stage_id: any = null,
    participant_id: any = null,
  ): Observable<HttpRes> {
    let params = new HttpParams();

    // Add stage_id if provided
    if (stage_id) {
      params = params.set('stage_id', stage_id);
    }

    // Add participant_id if provided
    if (participant_id) {
      params = params.set('participant_id', participant_id);
    }
    // Build the URL with event_category_id as path parameter
    const url = `${common_urls.recalculate_leaderboard}/${event_category_id}`;

    return this.httpClient.post<HttpRes>(url, { params, ...httpOptions }).pipe(
      shareReplay(),
      map((response: any) => {
        return {
          code: response.status,
          msg: response.body.msg,
          data: response.body,
        } as HttpRes;
      }),
    );
  }

  get_event_participants_award(
    is_queued: any = '',
    is_active: any = '',
    page_num: number = 1,
    per_page: number = 10,
    event_id: any = 0,
    category_id: any = 0,
    stage_ids: any = 0,
    search: any = '',
    verifier: boolean = false,
    verified_status?: any,
    is_deleted: boolean = false,
    is_elite: any = '',
    is_excluded: any = '',
    loanPlayer: boolean = false,
    stage_id: number = 0,
    participant_type_id: number | null = null,
    sort_by: any = '',
    sort_type: string | null = null,
    participant_ids: any = null,
  ): Observable<HttpRes> {
    is_queued = String(is_queued);
    is_active = String(is_active);
    const httpOption = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        tenant: 'tenant_a',
        auth:
          'Bearer ' +
          (typeof window !== 'undefined' && localStorage.getItem('token')
            ? localStorage.getItem('token')
            : ''),
      }),
      observe: 'response' as 'response',
    };
    let params = new HttpParams();
    if (event_id != 0 && event_id != '') {
      params = params.set('event_id', event_id);
    }
    if (category_id != 0 && category_id != '' && category_id != undefined) {
      params = params.set('category_id', category_id);
    }
    if (search != '') {
      params = params.set('search', search);
    }
    if (stage_ids != '') {
      params = params.set('stage_ids', stage_ids);
    }
    if (stage_id != 0 && stage_id != undefined) {
      params = params.set('stage_id', stage_id);
    }
    if (verified_status == 'ACCEPTED') {
      params = params.set('verified_status', 'ACCEPTED');
    }
    if (is_queued != 'undefined' && is_queued != 'null' && is_queued != '') {
      params = params.set('is_queued', is_queued);
    }
    if (is_active != 'undefined' && is_active != 'null' && is_active != '') {
      params = params.set('is_active', is_active);
    }
    if (typeof is_elite === 'boolean') {
      // If is_elite is true or false, set corresponding string
      params = params.set('is_elite', is_elite);
    }
    if (typeof is_excluded === 'boolean') {
      // If is_elite is true or false, set corresponding string
      params = params.set('is_excluded', is_excluded);
    }
    if (typeof loanPlayer === 'boolean') {
      params = params.set('loan_player', loanPlayer);
    }
    if (participant_type_id) {
      params = params.set('participant_type_id', participant_type_id);
    }
    if (sort_by != '') {
      params = params.set('sort_by', sort_by);
    }
    if (sort_type != null) {
      params = params.set('sort_type', sort_type);
    }
    if (participant_ids != null && participant_ids != '') {
      params = params.set('participant_ids', participant_ids);
    }
    params = params.set('page_num', page_num);
    params = params.set('per_page', per_page);
    params = params.set('verifier', verifier);
    params = params.set('is_deleted', is_deleted);
    return this.httpClient
      .get<HttpRes>(registration_urls.get_events_participants, {
        params,
        ...httpOption,
      })
      .pipe(
        shareReplay(),
        map((response: any) => {
          return {
            code: response.status,
            msg: response.body.msg,
            data: response.body,
          } as HttpRes;
        }),
      );
  }

  get_rule_setting(
    event_id: any,
    category_id: any,
    is_global: boolean = false,
    stage_id: any,
    config_type: any = 'RULE_SETTING',
  ): Observable<HttpRes> {
    let params = new HttpParams();
    if (event_id > 0) {
      params = params.set('event_id', event_id);
    }
    if (category_id != '') {
      params = params.set('category_id', category_id);
    }
    if (stage_id != '' && stage_id != null && stage_id != 0) {
      params = params.set('stage_id', stage_id);
    }
    params = params.set('is_global', is_global);
    params = params.set('config_type', config_type);

    return this.httpClient
      .get<HttpRes>(common_urls.get_rule_settings, {
        params,
        ...httpAuthOptions,
      })
      .pipe(
        shareReplay(),
        map((response: any) => {
          return {
            code: response.status,
            msg: response.msg,
            data: response.body.data,
          } as HttpRes;
        }),
      );
  }

  get_round_matches(
    stage_id: any,
    search: any = '',
    search_by: string = '',
    search_value: string = '',
  ) {
    let params = new HttpParams();
    params = params.set('stage_id', stage_id);
    params = params.set('group_by', 'round');
    params = params.set('show_organiser_details', true);
    if (search != null && search != '') {
      params = params.set('search', search);
    }
    if (search_by && search_value) {
      params = params.set('search_by', search_by);
      params = params.set('search_value', search_value);
    }

    return this.httpClient
      .get<HttpRes>(fixture_urls.get_round_matches, { params, ...httpOptions })
      .pipe(
        shareReplay(),
        map((response: any) => {
          return {
            code: response.status,
            msg: response.body.msg,
            data: response.body.data,
          } as HttpRes;
        }),
      );
  }

  // helper method to get the flags for the selected parents
  get_selected_parents_flag(
    info: any,
    is_bye: boolean = false,
    index: number = 0,
  ): string {
    const fallback = '';
    // const fallback = 'https://ettuadmin.stupaevents.com/assets/flags/flag4.png';

    if (is_bye) return fallback;

    const parentFlags = info?.selected_parents?.[index]?.parent_flags;
    return Array.isArray(parentFlags) && parentFlags[0]
      ? parentFlags[0]
      : fallback;
  }

  /**
   * Resolves a parent flag URL by matching a player's user_role_id against
   * selected_parents entries (used for doubles where array index !== player).
   */
  get_selected_parents_flag_by_user_role(
    selectedParents: Array<{
      user_role_id?: number | string;
      parent_flags?: string[];
    }> | null | undefined,
    userRoleId: number | string | null | undefined,
    isBye = false,
  ): string {
    const fallback = '';

    if (isBye || userRoleId == null || !Array.isArray(selectedParents)) {
      return fallback;
    }

    const match = selectedParents.find(
      (parent) => parent?.user_role_id === userRoleId,
    );
    const parentFlags = match?.parent_flags;

    return Array.isArray(parentFlags) && parentFlags[0]
      ? parentFlags[0]
      : fallback;
  }

  /**
   * Resolves a player's parent abbreviation as `MA/Club` (e.g. `LAT/NARON`)
   * by matching the player's user_role_id against selected_parents entries —
   * same matching rule as get_selected_parents_flag_by_user_role, so each
   * player in a doubles/team payload gets its own abbr. A player has one
   * entry per parent_role, so MA and Club are picked by role and joined with
   * `/` only when both exist. Entries without a parent_role fall back to the
   * first match's abbr.
   */
  get_selected_parents_abbr_by_user_role(
    selectedParents: Array<{
      user_role_id?: number | string;
      parent_abbr?: string;
      parent_role?: string;
    }> | null | undefined,
    userRoleId: number | string | null | undefined,
    isBye = false,
  ): string {
    const fallback = '';

    if (isBye || userRoleId == null || !Array.isArray(selectedParents)) {
      return fallback;
    }

    const matches = selectedParents.filter(
      (parent) => parent?.user_role_id === userRoleId,
    );
    const abbrForRole = (role: string): string =>
      matches.find((parent) => parent.parent_role === role)?.parent_abbr ?? '';
    const roleAbbr = this.joinParentDisplayValues(
      abbrForRole('Member Association'),
      abbrForRole('Club'),
    );
    if (roleAbbr) {
      return roleAbbr;
    }

    return matches[0]?.parent_abbr ?? fallback;
  }

  get_team_playing_order_with_matches(
    parent_match_id: any,
    playing_order_id: any,
    is_golden_match: any = null,
    include_stage_details: any = false,
    fetch_point_system: any = false,
  ): Observable<HttpRes> {
    let params = new HttpParams();
    if (parent_match_id > 0) {
      params = params.set('parent_match_id', parent_match_id);
    }
    if (playing_order_id != 0) {
      params = params.set('playing_order_id', playing_order_id ?? 'null');
    }
    if (is_golden_match != null) {
      params = params.set('is_golden_match', is_golden_match);
    }
    if (include_stage_details != null) {
      params = params.set('include_stage_details', include_stage_details);
    }
    if (fetch_point_system != null) {
      params = params.set('fetch_point_system', fetch_point_system);
    }
    return this.httpClient
      .get<HttpRes>(fixture_urls.get_team_playing_order_with_matches, {
        params,
        ...httpAuthOptions,
      })
      .pipe(
        shareReplay(),
        map((response: any) => {
          return {
            code: response.status,
            msg: response.body.msg,
            data: response.body,
          } as HttpRes;
        }),
      );
  }

  get_event_category_leaderboard(
    event_id: any,
    category_id: any,
    search: any = '',
  ): Observable<HttpRes> {
    let params = new HttpParams();

    // Add event_id and category_id as query parameters
    if (event_id) {
      params = params.set('event_id', event_id);
    }
    if (category_id) {
      params = params.set('category_id', category_id);
    }

    // Add search term for server-side participant name search
    if (search) {
      params = params.set('search', search);
    }
    // Build the URL with event_category_id as path parameter
    const url = `${common_urls.get_event_category_leaderboard}`;

    return this.httpClient.get<HttpRes>(url, { params, ...httpOptions }).pipe(
      shareReplay(),
      map((response: any) => {
        return {
          code: response.status,
          msg: response.body.msg,
          data: response.body,
        } as HttpRes;
      }),
    );
  }
  get_schedule_matches_view_details(params: {
    round_id: number;
    load_sub_matches?: boolean;
    load_event_official_data?: boolean;
    load_schedule_data?: boolean;
    order?: number;
    load_playing_order?: boolean;
    playing_order_id?: number;
    sort_by?: string | number;
    include_stage_details?: boolean;
    fetch_point_system?: boolean;
  }): Observable<HttpRes> {
    let httpprams = new HttpParams();

    httpprams = httpprams.set('round_id', params.round_id);

    if (params.load_sub_matches !== undefined) {
      httpprams = httpprams.set('load_sub_matches', params.load_sub_matches);
    }
    if (params.load_event_official_data !== undefined) {
      httpprams = httpprams.set(
        'load_event_official_data',
        params.load_event_official_data,
      );
    }
    if (params.load_schedule_data !== undefined) {
      httpprams = httpprams.set(
        'load_schedule_data',
        params.load_schedule_data,
      );
    }
    if (params.order !== undefined) {
      httpprams = httpprams.set('order', params.order);
    }
    if (params.load_playing_order !== undefined) {
      httpprams = httpprams.set(
        'load_playing_order',
        params.load_playing_order,
      );
    }
    if (params.playing_order_id !== undefined) {
      httpprams = httpprams.set('playing_order_id', params.playing_order_id);
    }
    if (params.sort_by !== undefined) {
      httpprams = httpprams.set('sort_by', params.sort_by);
    }

    if (params.include_stage_details !== undefined) {
      httpprams = httpprams.set(
        'include_stage_data',
        params.include_stage_details,
      );
    }

    if (params.fetch_point_system) {
      httpprams = httpprams.set(
        'fetch_point_system',
        params.fetch_point_system,
      );
    }

    return this.httpClient
      .get<HttpRes>(common_urls.get_matches, {
        params: httpprams,
        ...httpOptions,
        ...httpAuthOptions,
      })
      .pipe(
        shareReplay(),
        map((response: any) => {
          return {
            code: response.status,
            msg: response.msg,
            data: response.body,
          } as HttpRes;
        }),
      );
  }

  getResourceUserRolesFeaturePermissions(
    featureGroupId: number,
    resourceId: number,
    resourceType: string = 'EVENT',
    resultType: string = 'user_role',
    pageNum: number = 1,
    perPage: number = 10,
    listView: boolean = true,
    userRoleId?: number | string,
  ): Observable<HttpRes> {
    let params = new HttpParams()
      .set('feature_group_id', featureGroupId.toString())
      .set('resource_id', resourceId.toString())
      .set('resource_type', resourceType)
      .set('result_type', resultType)
      .set('page_num', pageNum.toString())
      .set('per_page', perPage.toString())
      .set('list_view', listView.toString());

    if (userRoleId !== undefined && userRoleId !== null) {
      params = params.set('user_role_id', userRoleId.toString());
    }

    return this.httpClient
      .get<HttpRes>(
        resource_permissions_urls.get_resource_user_roles_feature_permissions,
        {
          params: params,
          ...httpOptions,
          ...httpAuthOptions,
        },
      )
      .pipe(
        shareReplay(),
        map((response: any) => {
          return {
            code: response.status,
            msg: response.body?.msg || response.msg,
            data: response.body?.data || response.body,
          } as HttpRes;
        }),
      );
  }

  getParentDisplayValue(parent: any, abbrEnabled: boolean = false): string {
    if (!parent) {
      return '';
    }
    if (abbrEnabled) {
      return parent?.parent_abbr ?? '';
    }
    return parent?.parent_name || '';
  }

  getParentDisplayForRole(
    parents: any[],
    role: string,
    abbrEnabled: boolean = false,
    participant_id: any = null,
    separator: string = '\n',
  ): string {
    if (!Array.isArray(parents)) {
      return '';
    }
    const matches = parents.filter(
      (parent: any) => parent && parent.parent_role === role,
    );
    if (!matches.length) {
      return '';
    }
    if (participant_id === 3 || participant_id === 4) {
      const values = matches.map((parent: any) =>
        this.getParentDisplayValue(parent, abbrEnabled),
      );
      // .filter((value: string) => value && value !== '--');
      if (values.length) {
        return values.join(separator);
      }
    }
    return this.getParentDisplayValue(matches[0], abbrEnabled);
  }

  getParentDisplayForRoleAndPlayerId(
    parents: any[],
    role: string,
    playerId: number | string,
    abbrEnabled: boolean = false,
  ): string {
    if (!Array.isArray(parents)) {
      return '';
    }

    // Filter parents by both role and player ID
    const match = parents.find(
      (parent: any) =>
        parent &&
        parent.parent_role === role &&
        parent.user_role_id === playerId,
    );

    if (!match) {
      return '';
    }

    return this.getParentDisplayValue(match, abbrEnabled);
  }

  /** Joins MA and Club labels with `/` only when both values are present. */
  joinParentDisplayValues(primary: string, secondary: string): string {
    const first = this.normalizeParentDisplay(primary);
    const second = this.normalizeParentDisplay(secondary);
    if (first && second) {
      return `${first}/${second}`;
    }
    if (first) {
      return first;
    }
    if (second) {
      return second;
    }
    return '';
  }

  /**
   * Visible parent labels for a participant (or one doubles player), respecting
   * displayable-entity settings. Never emits a leading/trailing `/`.
   */
  formatDisplayableParents(
    parents: any[],
    displayableEntity: string | null,
    displayableEntity2: string | null,
    abbrEnabled: boolean,
    participantTypeId: number | string | null,
    playerId?: number | string | null,
  ): string {
    if (!Array.isArray(parents) || parents.length === 0) {
      return '';
    }
    const typeId = Number(participantTypeId);
    const isDoubles = typeId === 3 || typeId === 4;
    const memberAssociation = this.parentValueForVisibleRole(
      parents,
      'Member Association',
      displayableEntity,
      displayableEntity2,
      abbrEnabled,
      typeId,
      isDoubles,
      playerId,
    );
    const club = this.parentValueForVisibleRole(
      parents,
      'Club',
      displayableEntity,
      displayableEntity2,
      abbrEnabled,
      typeId,
      isDoubles,
      playerId,
    );
    return this.joinParentDisplayValues(memberAssociation, club);
  }

  private parentValueForVisibleRole(
    parents: any[],
    role: string,
    displayableEntity: string | null,
    displayableEntity2: string | null,
    abbrEnabled: boolean,
    typeId: number,
    isDoubles: boolean,
    playerId?: number | string | null,
  ): string {
    if (displayableEntity !== role && displayableEntity2 !== role) {
      return '';
    }
    if (isDoubles) {
      if (playerId == null) {
        return '';
      }
      return this.getParentDisplayForRoleAndPlayerId(
        parents,
        role,
        playerId,
        abbrEnabled,
      );
    }
    return this.getParentDisplayForRole(parents, role, abbrEnabled, typeId);
  }

  private normalizeParentDisplay(value: string): string {
    const trimmed = (value ?? '').trim();
    if (trimmed === '' || trimmed === '--') {
      return '';
    }
    return trimmed;
  }

  get_events_documents_sponsors(event_id?: any): Observable<HttpRes> {
    let params = new HttpParams();
    if (event_id != 0 && event_id != '') {
      params = params.set('event_id', event_id);
    }

    return this.httpClient
      .get<HttpRes>(common_urls.get_events_documents_sponsors, {
        params,
        ...httpOptions,
        ...httpAuthOptions,
      })
      .pipe(
        shareReplay(),
        map((response: any) => {
          return {
            code: response.status,
            msg: response.body.msg,
            data: response.body.data,
          } as HttpRes;
        }),
      );
  }

  /**
   * Get player events (tournaments or leagues)
   * @param event_type - 'T' for Tournament, 'L' for League
   * @param page - Page number (default: 1)
   * @param limit - Number of items per page (default: 10)
   * @param order_by - Field to order by (default: 'event_start_date')
   */
  getPlayerEvents(
    event_type: string = 'T',
    page: number = 1,
    limit: number = 10,
    order_by: string = 'event_start_date',
  ): Observable<HttpRes> {
    let params = new HttpParams();
    params = params.set('event_type', event_type);
    params = params.set('page', page.toString());
    params = params.set('limit', limit.toString());
    params = params.set('order_by', order_by);

    return this.httpClient
      .get<HttpRes>(common_urls.get_player_events, {
        params,
        ...httpOptions,
        ...httpAuthOptions,
      })
      .pipe(
        shareReplay(),
        map((response: any) => {
          return {
            code: response.status,
            msg: response.body.msg,
            data: response.body.data,
          } as HttpRes;
        }),
      );
  }

  /**
   * Get player performance metrics by year
   * @param year - Year to fetch metrics for (e.g. 2023)
   */
  getPerformanceMetrics(year: number | string): Observable<HttpRes> {
    const headers = new HttpHeaders({
      accept: 'application/json',
      tenant: 'dev',
      Auth:
        'Bearer ' +
        (typeof window !== 'undefined' &&
        typeof window.localStorage !== 'undefined'
          ? localStorage.getItem('token')
          : ''),
    });

    let params = new HttpParams().set('year', year.toString());

    return this.httpClient
      .get<HttpRes>(common_urls.get_performance_metrics, {
        headers,
        params,
        observe: 'response' as 'response',
      })
      .pipe(
        shareReplay(),
        map((response: any) => {
          return {
            code: response.status,
            msg: response.body.msg,
            data: response.body.data,
          } as HttpRes;
        }),
      );
  }

  getRecentMatches(page: number = 1, limit: number = 5): Observable<HttpRes> {
    const headers = new HttpHeaders({
      accept: 'application/json',
      tenant: 'dev',
      Auth:
        'Bearer ' +
        (typeof window !== 'undefined' &&
        typeof window.localStorage !== 'undefined'
          ? localStorage.getItem('webToken')
          : ''),
    });

    let params = new HttpParams()
      .set('page', page.toString())
      .set('limit', limit.toString())
      .set('event_type', EVENT_TYPE_CODE.TOURNAMENT);

    return this.httpClient
      .get<HttpRes>(common_urls.get_recent_matches, {
        headers,
        params,
        observe: 'response' as 'response',
      })
      .pipe(
        shareReplay(),
        map((response: any) => {
          return {
            code: response.status,
            msg: response.body.msg,
            data: response.body.data,
          } as HttpRes;
        }),
      );
  }

  searchPlayers(search_value: string): Observable<HttpRes> {
    let params = new HttpParams()
      .set('page_num', '1')
      .set('per_page', '100')
      .set('is_active', 'true')
      .set('exclude_self', true);
    const trimmed = search_value.trim();
    if (trimmed) {
      params = params.set('search_value', trimmed);
    }

    return this.httpClient
      .get<HttpRes>(registration_urls.list_all_players, {
        params,
        ...httpOptions,
        ...httpAuthOptions,
      })
      .pipe(
        shareReplay(),
        map((response: any) => {
          const body = response?.body ?? response;
          return {
            code: response?.status ?? body?.code,
            msg: body?.msg,
            data: body?.data ?? body,
          } as HttpRes;
        }),
      );
  }

  private playerInfo$?: Observable<HttpRes>;
  private playerInfoToken = '';
  private playerInfoPending = false;

  /**
   * One shared response per signed-in user. Pass `refresh` when the data may have
   * changed (profile saved, edit form opened, verification re-check); a request
   * already in flight is reused since it is fresh anyway.
   */
  getPlayerInfo(refresh = false): Observable<HttpRes> {
    const token = getStoredAccessToken();
    const cached$ = this.playerInfo$;
    const reuseCached =
      cached$ && token === this.playerInfoToken && (!refresh || this.playerInfoPending);
    if (reuseCached) {
      return cached$;
    }

    this.playerInfoToken = token;
    this.playerInfoPending = true;
    const request$: Observable<HttpRes> = this.httpClient
      .get<HttpRes>(common_urls.get_player_info, {
        ...httpOptions,
        ...httpAuthOptions,
      })
      .pipe(
        map((response: any) => {
          return {
            code: response.status,
            msg: response.body.msg,
            data: response.body.data,
          } as HttpRes;
        }),
        catchError((error) => {
          if (this.playerInfo$ === request$) {
            this.playerInfo$ = undefined;
          }
          return throwError(() => error);
        }),
        finalize(() => {
          if (this.playerInfo$ === request$ || !this.playerInfo$) {
            this.playerInfoPending = false;
          }
        }),
        shareReplay(1),
      );
    this.playerInfo$ = request$;
    return request$;
  }

  getPlayerAnalytics(
    user_role_id?: number,
    only_analytics: boolean = false,
  ): Observable<HttpRes> {
    let params = new HttpParams();
    if (user_role_id) {
      params = params.set('user_role_id', user_role_id);
    }
    if (only_analytics) {
      params = params.set('only_analytics', 'true');
    }

    return this.httpClient
      .get<HttpRes>(common_urls.get_player_info, {
        params,
        ...httpOptions,
        ...httpAuthOptions,
      })
      .pipe(
        shareReplay(),
        map((response: any) => {
          return {
            code: response.status,
            msg: response.body.msg,
            data: response.body.data,
          } as HttpRes;
        }),
      );
  }

  // Cached observable so the HTTP request fires only once per app session.
  // Any subsequent caller (LoginComponent, SignupComponent) gets the replayed
  // value from shareReplay(1) without triggering a new network request.
  // On failure the cache is cleared so the next caller retries from scratch
  // instead of replaying a broken observable forever.
  private footerConfig$: Observable<HttpRes> | null = null;

  getFooterConfig(): Observable<HttpRes> {
    if (!this.footerConfig$) {
      this.footerConfig$ = this.httpClient
        .get<HttpRes>(this.getConfigUrl, {
          ...httpOptions,
          ...httpAuthOptions,
        })
        .pipe(
          map((response: any) => {
            return {
              code: response.status,
              msg: response.body.msg,
              data: response.body.data,
            } as HttpRes;
          }),
          catchError((error) => {
            this.footerConfig$ = null; // clear cache so next caller retries
            return throwError(() => error);
          }),
          shareReplay(1), // cache after map so subscribers get the transformed HttpRes, not the raw HttpResponse
        );
    }
    return this.footerConfig$;
  }

  /**
   * Fetches event finance settings (pricing, fees, self-registration config, etc.)
   * Used by self-registration screen (app-self-registration).
   */
  getEventFinanceSettings(event_id: any): Observable<HttpRes> {
    let params = new HttpParams();
    if (event_id) {
      params = params.set('event_id', event_id);
    }

    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      auth:
        'Bearer ' +
        (typeof window !== 'undefined' && typeof window.localStorage !== 'undefined'
          ? localStorage.getItem('token')
          : ''),
      'event-Id': event_id ? event_id.toString() : '',
      tenant: this.tenantCode || environment.tenant_Name || 'dev',
      source: 'admin',
      language: 'en',
      'ngrok-skip-browser-warning': 'true',
      Accept: 'application/json, text/plain, */*',
    });

    return this.httpClient
      .get<HttpRes>(common_urls.get_event_finance_settings, {
        params,
        headers,
        observe: 'response' as 'response',
      })
      .pipe(
        shareReplay(),
        map((response: any) => {
          return {
            code: response.status,
            msg: response.body?.msg || response.msg,
            data: response.body?.data || response.body,
          } as HttpRes;
        }),
      );
  }

  getCombinedStandings(payload: any): Observable<HttpRes> {
    let httpParams = new HttpParams();
    httpParams = httpParams.set('category_ids', payload.category_ids);
    httpParams = httpParams.set('sort_by', payload.sort_by);
    httpParams = httpParams.set('page', payload.page);
    httpParams = httpParams.set('per_page', payload.per_page);
    if (payload.sort_order) {
      httpParams = httpParams.set('sort_order', payload.sort_order);
    }
    if (payload.search) {
      httpParams = httpParams.set('search', payload.search);
    }
    if (payload.date_from) {
      httpParams = httpParams.set('date_from', payload.date_from);
    }
    if (payload.date_to) {
      httpParams = httpParams.set('date_to', payload.date_to);
    }
    if (payload.filter_rank_min !== undefined && payload.filter_rank_min !== null) {
      httpParams = httpParams.set('filter_rank_min', payload.filter_rank_min);
    }
    if (payload.filter_rank_max !== undefined && payload.filter_rank_max !== null) {
      httpParams = httpParams.set('filter_rank_max', payload.filter_rank_max);
    }
    if (payload.season_id !== undefined && payload.season_id !== null) {
      httpParams = httpParams.set('season_id', payload.season_id);
    }
    if (Array.isArray(payload.filter_club_id) && payload.filter_club_id.length > 0) {
      httpParams = httpParams.set('parents', payload.filter_club_id.join(','));
    }
    if (Array.isArray(payload.event_ids) && payload.event_ids.length > 0) {
      httpParams = httpParams.set('event_ids', payload.event_ids.join(','));
    }

    const url = `${rnr_end_points.combined_standings}?${httpParams.toString()}`;
    return this.httpClient
      .get<HttpRes>(url, {
        ...httpOptions,
        ...httpAuthOptions,
      })
      .pipe(
        shareReplay(),
        map((response: any) => {
          return {
            code: response.status,
            msg: response.body?.msg ?? response.msg,
            data: response.body?.data ?? response.body,
          } as HttpRes;
        }),
      );
  }
}
